import { Component, Input, OnInit, OnChanges, Output, EventEmitter, ViewChild } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { BaseComponent } from '../base/base.component';
import { PurchasedLicensesInfo } from '../data/purchased-licenses-info';
import { json2csv } from 'json-2-csv';
import { FileUtilities } from '../utilities/file-utilities';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { OrganizationService } from '../organization.service';
import { RedirectService } from '../redirect.service';
import { DataService } from '../data/data.service';
import { Location } from '@angular/common';
import { LicenseFilteringService } from '../services/license-filtering.service';

export interface ISortOrder
{
    readonly orderBy: string;
    ascending: boolean;
}

@Component({
    selector: 'app-org-license-details',
    templateUrl: './org-license-details.component.html',
    styleUrls: ['./org-license-details.component.css']
})
export class OrgLicenseDetailsComponent extends BaseComponent implements OnInit, OnChanges
{

     @Input() organizationId: number;
     @Input() organizationDescription: string = '';
     @Output() licenseSaved = new EventEmitter<PurchasedLicensesInfo>();

    // License and sales rep data (moved from other components)
    allLicenses: PurchasedLicensesInfo[] = [];
    filteredAndSortedLicenses: PurchasedLicensesInfo[] = [];
    orgSalesRep: {
        apmName: string;
        apmEmail: string;
    } = {
            apmName: '',
            apmEmail: ''
        };
    loading: boolean = false;
    filtering: boolean = false;
    areLicensesFetched: boolean = false;
    isSalesRepFetched: boolean = false;
    ignoreLicenseDates: boolean = false;

    currentSort: ISortOrder = { orderBy: 'LicenseEndDate', ascending: true };
    calculationDate: Date = new Date();

     @ViewChild('licenseChangeAnalysisModal') licenseChangeAnalysisModal: any;
     @ViewChild('addOrEditLicensesModal') addOrEditLicensesModal: any;
     @ViewChild('columnSettingsDropdown') columnSettingsDropdown: any;

    // Filter properties
    excludeGracePeriod: boolean = false;
    selectedPurchaseType: string = '';
    selectedPurchaseLevel: string = '';

    // Available filter options
    availablePurchaseTypes: string[] = [];
    availablePurchaseLevels: string[] = [];

    // Column visibility configuration
    columnConfig = [
        { id: 'PurchasingOrganizationDescription', label: 'Purchasing Organization', visible: true, defaultVisible: true, sortable: true },
        { id: 'Quantity', label: 'Quantity', visible: true, defaultVisible: true, sortable: true },
        { id: 'PurchaseDate', label: 'Purchase Date', visible: true, defaultVisible: true, sortable: true },
        { id: 'LicenseStartDate', label: 'License Start Date', visible: false, defaultVisible: false, sortable: true },
        { id: 'LicenseEndDate', label: 'License End Date', visible: true, defaultVisible: true, sortable: true },
        { id: 'IsInGracePeriod', label: 'In Grace Period', visible: true, defaultVisible: true, sortable: true },
        { id: 'PurchaseDollarAmount', label: 'Purchase Amount', visible: true, defaultVisible: true, sortable: true },
        { id: 'LicenseLengthYears', label: 'License Length (Years)', visible: true, defaultVisible: true, sortable: true },
        { id: 'ARR', label: 'ARR', visible: true, defaultVisible: true, sortable: true },
        { id: 'CostPerLicense', label: 'Cost Per License', visible: true, defaultVisible: true, sortable: true },
        { id: 'LicensePurchaseType', label: 'Purchase Type', visible: false, defaultVisible: false, sortable: true },
        { id: 'PurchaseLevel', label: 'Purchase Level', visible: false, defaultVisible: false, sortable: true },
        { id: 'PurchaseOrderID', label: 'Purchase Order ID', visible: false, defaultVisible: false, sortable: true },
        { id: 'DealID', label: 'Deal ID', visible: false, defaultVisible: false, sortable: true },
        { id: 'InvoiceID', label: 'Invoice ID', visible: false, defaultVisible: false, sortable: true },
        { id: 'Note', label: 'Note', visible: false, defaultVisible: false, sortable: false }
    ];

    // Individual column visibility booleans for easier template usage
    isPurchasingOrganizationDescriptionVisible: boolean = true;
    isQuantityVisible: boolean = true;
    isPurchaseDateVisible: boolean = true;
    isLicenseStartDateVisible: boolean = false;
    isLicenseEndDateVisible: boolean = true;
    isIsInGracePeriodVisible: boolean = true;
    isPurchaseDollarAmountVisible: boolean = true;
    isLicenseLengthYearsVisible: boolean = true;
    isARRVisible: boolean = true;
    isCostPerLicenseVisible: boolean = true;
    isLicensePurchaseTypeVisible: boolean = false;
    isPurchaseLevelVisible: boolean = false;
    isPurchaseOrderIDVisible: boolean = false;
    isDealIDVisible: boolean = false;
    isInvoiceIDVisible: boolean = false;
    isNoteVisible: boolean = false;


    constructor(
        protected route: ActivatedRoute,
        protected authService: AuthService,
        protected organizationService: OrganizationService,
        protected redirectService: RedirectService,
        protected router: Router,
        protected dataService: DataService,
        protected location: Location,
        private licenseFilteringService: LicenseFilteringService
    )
    {
        super(route, authService, organizationService, redirectService, router, dataService, location);
    }

    ngOnInit()
    {
        this.loadColumnPreferences();
        this.updateAvailableFilterOptions();
        this.updateFilteredAndSortedLicenses();

         // Auto-fetch licenses for internal users
         if (this.isInternal())
         {
             this.fetchAllLicenses();
             this.getOrganizationSalesRep();
         }
    }

    ngOnChanges()
    {
        this.updateAvailableFilterOptions();
        this.updateFilteredAndSortedLicenses();
    }

    updateSort(sortOrder: ISortOrder)
    {
        this.currentSort = sortOrder;
        this.filteredAndSortedLicenses =  this.licenseFilteringService.sortLicenses(this.filteredAndSortedLicenses, sortOrder.orderBy, sortOrder.ascending);
    }

    updateFilteredAndSortedLicenses(): void
    {
        if (!this.allLicenses || !Array.isArray(this.allLicenses))
        {
            this.filteredAndSortedLicenses = [];
            return;
        }

        // Show filtering spinner
        this.filtering = true;

        // Use setTimeout to ensure the UI updates before the heavy filtering operation
        setTimeout(() => {
            // Use the service to filter and sort licenses
            this.filteredAndSortedLicenses = this.licenseFilteringService.filterAndSortLicenses(
                this.allLicenses,
                {
                    asOfDate: this.calculationDate,
                    ignoreLicenseDates: this.ignoreLicenseDates,
                    gracePeriodDays: 60,
                    excludeGracePeriod: this.excludeGracePeriod,
                    selectedPurchaseType: this.selectedPurchaseType,
                    selectedPurchaseLevel: this.selectedPurchaseLevel,
                    sortField: this.currentSort.orderBy,
                    sortAscending: this.currentSort.ascending
                }
            );

            // Hide filtering spinner
            this.filtering = false;

            // Debug: Print the final count
            console.log('filteredAndSortedLicenses count:', this.filteredAndSortedLicenses.length);
        }, 0);
    }

    getPropertyValue(obj: any, propertyName: string): any
    {
        if (propertyName === 'CostPerLicense')
        {
            if (!obj.Quantity || obj.Quantity <= 0 || !obj.ARR || obj.ARR <= 0)
            {
                return 0;
            }
            return obj.ARR / obj.Quantity;
        }
        return obj[propertyName];
    }

    formatDate(date: Date | string): string
    {
        if (!date) return '';

        let dateObj: Date;
        if (typeof date === 'string')
        {
            // Handle date strings by parsing them as local dates to avoid timezone issues
            const parts = date.split('T')[0].split('-');
            dateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        } else
        {
            dateObj = date;
        }

        return dateObj.toLocaleDateString();
    }

    formatCurrency(amount: number): string
    {
        if (amount === null || amount === undefined) return '';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    }

    formatCurrencyNoCents(amount: number): string
    {
        if (amount === null || amount === undefined) return '';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
    }

    formatNumber(num: number): string
    {
        if (num === null || num === undefined) return '';
        return num.toLocaleString();
    }

    formatCostPerLicense(license: PurchasedLicensesInfo): string
    {
        if (!license.Quantity || license.Quantity <= 0)
        {
            return 'N/A';
        }

        const costPerLicense = license.ARR / license.Quantity;
        if (costPerLicense <= 0)
        {
            return 'N/A';
        }

        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(costPerLicense);
    }

    formatCostPerLicenseNoCents(license: PurchasedLicensesInfo): string
    {
        if (!license.Quantity || license.Quantity <= 0)
        {
            return 'N/A';
        }

        const costPerLicense = license.ARR / license.Quantity;
        if (costPerLicense <= 0)
        {
            return 'N/A';
        }

        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(costPerLicense);
    }

    formatCostPerLicenseForCSV(license: PurchasedLicensesInfo): string
    {
        if (!license.Quantity || license.Quantity <= 0)
        {
            return '';
        }

        const costPerLicense = license.ARR / license.Quantity;
        if (costPerLicense <= 0)
        {
            return '';
        }

        return costPerLicense.toFixed(2);
    }

    get totalQuantity(): number
    {
        if (!this.filteredAndSortedLicenses || !Array.isArray(this.filteredAndSortedLicenses))
        {
            return 0;
        }
        return this.filteredAndSortedLicenses.reduce((sum, license) => sum + (license.Quantity || 0), 0);
    }

    get totalPurchaseAmount(): number
    {
        if (!this.filteredAndSortedLicenses || !Array.isArray(this.filteredAndSortedLicenses))
        {
            return 0;
        }
        return this.filteredAndSortedLicenses.reduce((sum, license) => sum + (license.PurchaseDollarAmount || 0), 0);
    }

    get totalARR(): number
    {
        if (!this.filteredAndSortedLicenses || !Array.isArray(this.filteredAndSortedLicenses))
        {
            return 0;
        }
        return this.filteredAndSortedLicenses.reduce((sum, license) => sum + (license.ARR || 0), 0);
    }

    get averageCostPerLicense(): number
    {
        if (!this.totalQuantity || this.totalQuantity <= 0 || !this.totalARR || this.totalARR <= 0)
        {
            return 0;
        }
        return this.totalARR / this.totalQuantity;
    }

    get isFiltering(): boolean
    {
        return this.excludeGracePeriod ||
            this.selectedPurchaseType !== '' ||
            this.selectedPurchaseLevel !== '';
    }

    onDateChanged()
    {
        // Update the filtered and sorted licenses when the date changes
        this.updateFilteredAndSortedLicenses();
    }

    updateAvailableFilterOptions()
    {
        if (!this.allLicenses || !Array.isArray(this.allLicenses))
        {
            this.availablePurchaseTypes = [];
            this.availablePurchaseLevels = [];
            return;
        }

        // Get unique purchase types
        const purchaseTypes = new Set<string>();
        const purchaseLevels = new Set<string>();

        this.allLicenses.forEach(license =>
        {
            if (license.LicensePurchaseType)
            {
                purchaseTypes.add(license.LicensePurchaseType);
            }
            if (license.PurchaseLevel)
            {
                purchaseLevels.add(license.PurchaseLevel);
            }
        });

        this.availablePurchaseTypes = Array.from(purchaseTypes).sort();
        this.availablePurchaseLevels = Array.from(purchaseLevels).sort();
    }


    downloadCSV()
    {
        if (!this.filteredAndSortedLicenses || this.filteredAndSortedLicenses.length === 0)
        {
            return;
        }

        // Prepare data for CSV export - fixed columns
        const csvData = this.filteredAndSortedLicenses.map(license =>
        {
            const csvRow: any = {};

            // Fixed columns for CSV export
            csvRow['Purchasing Organization'] = license.PurchasingOrganizationDescription;
            csvRow['Quantity'] = license.Quantity;
            csvRow['Purchase Date'] = this.formatDate(license.PurchaseDate);
            csvRow['License End Date'] = this.formatDate(license.LicenseEndDate);
            csvRow['In Grace Period'] = license.IsInGracePeriod ? 'Yes' : 'No';
            csvRow['Purchase Amount'] = license.PurchaseDollarAmount;
            csvRow['License Length (Years)'] = license.LicenseLengthYears;
            csvRow['ARR'] = license.ARR;
            csvRow['Cost Per License'] = this.formatCostPerLicenseForCSV(license);

            return csvRow;
        });

        json2csv(csvData, (err, csv) =>
        {
            if (err)
            {
                console.error('Error generating CSV:', err);
                return;
            }

            const fileName = `licenses_${new Date().toISOString().split('T')[0]}.csv`;
            FileUtilities.saveFile(csv, fileName, 'text/csv');
        });
    }

    isSeanUser(): boolean
    {
        return this.authService.session?.Email === 'sean@alegralearning.com';
    }


    hasNote(license: PurchasedLicensesInfo): boolean
    {
        return license.Note && license.Note.trim().length > 0;
    }

    getNoteTooltip(license: PurchasedLicensesInfo): string
    {
        if (!this.hasNote(license))
        {
            return '';
        }
        return license.Note;
    }

    isLicenseExpired(license: PurchasedLicensesInfo): boolean
    {
        if (!license.LicenseEndDate)
        {
            return false;
        }

        const endDate = this.parseDateAsLocal(license.LicenseEndDate);
        const today = new Date();

        // Reset time to start of day for accurate date comparison
        today.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        return endDate < today;
    }

    private parseDateAsLocal(dateString: string | Date): Date
    {
        if (typeof dateString === 'string')
        {
            // Parse date string as local date (YYYY-MM-DD format)
            const [year, month, day] = dateString.split('-').map(Number);
            return new Date(year, month - 1, day); // month is 0-indexed
        }
        return new Date(dateString);
    }


     // License data fetching methods (moved from other components)
     fetchAllLicenses(): void
     {
         // Only fetch licenses for internal users
         if (!this.isInternal())
         {
             return;
         }

         this.loading = true;
         this.dataService.getOrganizationAllLicenses(this.organizationId).subscribe(
            licensesArray =>
            {
                if (Array.isArray(licensesArray))
                {
                    this.allLicenses = licensesArray;
                } else
                {
                    this.allLicenses = [];
                }
                this.areLicensesFetched = true;
                this.loading = false;
                this.updateAvailableFilterOptions();
                this.updateFilteredAndSortedLicenses();
            },
            error =>
            {
                this.handleSaveError();
                this.loading = false;
            }
        );
    }



     onLicenseDateChanged(asOfDate: Date): void
     {
         console.log('Date changed');
         // Update the calculation date and recalculate filtering
         this.calculationDate = asOfDate;
         this.updateFilteredAndSortedLicenses();
     }

     onIgnoreLicenseDatesChanged(ignoreLicenseDates: boolean): void
     {
         this.ignoreLicenseDates = ignoreLicenseDates;
         // Update the filtered and sorted licenses with the new setting
         this.updateFilteredAndSortedLicenses();
     }

    getOrganizationSalesRep(): void
    {
        this.dataService.getOrganizationSalesRep(this.organizationId).subscribe(
            orgArray =>
            {
                if (Array.isArray(orgArray) && orgArray.length > 0)
                {
                    const org = orgArray[0];
                    this.orgSalesRep = {
                        apmName: org.Name || '',
                        apmEmail: org.EmailAddress || ''
                    };
                } else
                {
                    this.orgSalesRep = {
                        apmName: '',
                        apmEmail: ''
                    };
                }
                this.isSalesRepFetched = true;
            },
            error =>
            {
                this.handleSaveError();
            }
        );
    }

    private isOpeningEditModal = false;

    onEditLicense(license: PurchasedLicensesInfo, event?: Event): void
    {
        if (event)
        {
            event.stopPropagation();
        }
        
        // Prevent multiple calls
        if (this.isOpeningEditModal) {
            return;
        }
        
        this.isOpeningEditModal = true;
        
        // Open the edit modal
        if (this.addOrEditLicensesModal) {
            this.addOrEditLicensesModal.openForEdit(license);
        }
        
        // Reset the flag after a short delay
        setTimeout(() => {
            this.isOpeningEditModal = false;
        }, 1000);
    }

    openNewLicenseModal(): void
    {
        if (this.addOrEditLicensesModal) {
            this.addOrEditLicensesModal.openForNew();
        }
    }

    onLicenseSaved(savedLicense: PurchasedLicensesInfo): void
    {
        // Update the allLicenses list
        const existingIndex = this.allLicenses.findIndex(l => l.PurchasedLicensesID === savedLicense.PurchasedLicensesID);
        
        if (existingIndex >= 0) {
            // Update existing license
            this.allLicenses[existingIndex] = savedLicense;
        } else {
            // Add new license
            this.allLicenses.push(savedLicense);
        }
        
        // Update the filtered and sorted list
        this.updateFilteredAndSortedLicenses();
        
        // Emit the saved license to parent components
        this.licenseSaved.emit(savedLicense);
    }

    isInternal(): boolean
    {
        return this.authService.isInternal();
    }

    // Column visibility management methods
    loadColumnPreferences(): void {
        const savedPreferences = localStorage.getItem('licenseTableColumnPreferences');
        if (savedPreferences) {
            try {
                const preferences = JSON.parse(savedPreferences);
                this.columnConfig.forEach(column => {
                    if (preferences[column.id] !== undefined) {
                        column.visible = preferences[column.id];
                    }
                });
            } catch (error) {
                console.error('Error loading column preferences:', error);
            }
        }
        this.updateColumnVisibilityBooleans();
    }

    saveColumnPreferences(): void {
        const preferences: { [key: string]: boolean } = {};
        this.columnConfig.forEach(column => {
            preferences[column.id] = column.visible;
        });
        localStorage.setItem('licenseTableColumnPreferences', JSON.stringify(preferences));
    }

    toggleColumnVisibility(columnId: string): void {
        const column = this.columnConfig.find(col => col.id === columnId);
        if (column) {
            column.visible = !column.visible;
            this.updateColumnVisibilityBooleans();
            this.saveColumnPreferences();
        }
    }

    selectAllColumns(): void {
        this.columnConfig.forEach(column => {
            column.visible = true;
        });
        this.updateColumnVisibilityBooleans();
        this.saveColumnPreferences();
        this.closeDropdown();
    }

    deselectAllColumns(): void {
        this.columnConfig.forEach(column => {
            column.visible = false;
        });
        this.updateColumnVisibilityBooleans();
        this.saveColumnPreferences();
        this.closeDropdown();
    }

    resetToDefaultColumns(): void {
        this.columnConfig.forEach(column => {
            column.visible = column.defaultVisible;
        });
        this.updateColumnVisibilityBooleans();
        this.saveColumnPreferences();
        this.closeDropdown();
    }

    private closeDropdown(): void {
        if (this.columnSettingsDropdown && this.columnSettingsDropdown.isOpen) {
            this.columnSettingsDropdown.toggle();
        }
    }

    getVisibleColumns(): any[] {
        return this.columnConfig.filter(column => column.visible);
    }

    // Update all boolean properties from columnConfig
    private updateColumnVisibilityBooleans(): void {
        this.isPurchasingOrganizationDescriptionVisible = this.columnConfig.find(c => c.id === 'PurchasingOrganizationDescription')?.visible || false;
        this.isQuantityVisible = this.columnConfig.find(c => c.id === 'Quantity')?.visible || false;
        this.isPurchaseDateVisible = this.columnConfig.find(c => c.id === 'PurchaseDate')?.visible || false;
        this.isLicenseStartDateVisible = this.columnConfig.find(c => c.id === 'LicenseStartDate')?.visible || false;
        this.isLicenseEndDateVisible = this.columnConfig.find(c => c.id === 'LicenseEndDate')?.visible || false;
        this.isIsInGracePeriodVisible = this.columnConfig.find(c => c.id === 'IsInGracePeriod')?.visible || false;
        this.isPurchaseDollarAmountVisible = this.columnConfig.find(c => c.id === 'PurchaseDollarAmount')?.visible || false;
        this.isLicenseLengthYearsVisible = this.columnConfig.find(c => c.id === 'LicenseLengthYears')?.visible || false;
        this.isARRVisible = this.columnConfig.find(c => c.id === 'ARR')?.visible || false;
        this.isCostPerLicenseVisible = this.columnConfig.find(c => c.id === 'CostPerLicense')?.visible || false;
        this.isLicensePurchaseTypeVisible = this.columnConfig.find(c => c.id === 'LicensePurchaseType')?.visible || false;
        this.isPurchaseLevelVisible = this.columnConfig.find(c => c.id === 'PurchaseLevel')?.visible || false;
        this.isPurchaseOrderIDVisible = this.columnConfig.find(c => c.id === 'PurchaseOrderID')?.visible || false;
        this.isDealIDVisible = this.columnConfig.find(c => c.id === 'DealID')?.visible || false;
        this.isInvoiceIDVisible = this.columnConfig.find(c => c.id === 'InvoiceID')?.visible || false;
        this.isNoteVisible = this.columnConfig.find(c => c.id === 'Note')?.visible || false;
    }
} 